/**
 * Store offer catalog — mirrors contracts/monetization/offers.json.
 * Stripe Price ids live in env vars named by stripe_price_env; amounts are
 * resolved at runtime from GET /api/billing/plans.
 */

export const FREE_COMPUTE_CREDITS = 5;
export const FREE_ROOM_SLOTS = 1;
export const MAX_ROOM_CAP = 100;

export type OfferId = 'credits' | 'room_1_50' | 'room_2_120' | 'unlimited';

export type OfferKind = 'variable_credits' | 'bundle' | 'unlimited';

export interface OfferGrants {
  credits_per_unit?: number;
  credits?: number;
  extra_rooms?: number;
  panel_placement?: boolean;
  unlimited_compute?: boolean;
  unlimited_rooms?: boolean;
}

export interface StoreOffer {
  id: OfferId;
  name: string;
  description: string;
  kind: OfferKind;
  stripePriceEnv: string;
  grants: OfferGrants;
}

export const CREDIT_QUANTITY = {
  min: 1,
  max: 999999,
  step: 1,
  default: 1,
} as const;

export const STORE_OFFERS: StoreOffer[] = [
  {
    id: 'credits',
    name: '10 compute credits',
    description: 'A pack of 10 credits. One credit runs one simulation. Does not unlock panel placement or extra rooms.',
    kind: 'variable_credits',
    stripePriceEnv: 'STRIPE_PRICE_CREDIT_PACK',
    grants: { credits_per_unit: 10 },
  },
  {
    id: 'room_1_50',
    name: '+1 room · 25 credits · panels',
    description: 'One extra room forever, 25 compute credits, and panel placement unlocked.',
    kind: 'bundle',
    stripePriceEnv: 'STRIPE_PRICE_BUNDLE_ROOM_1_50',
    grants: { extra_rooms: 1, credits: 25, panel_placement: true },
  },
  {
    id: 'room_2_120',
    name: '+2 rooms · 60 credits · panels',
    description: 'Two extra rooms forever, 60 compute credits, and panel placement unlocked.',
    kind: 'bundle',
    stripePriceEnv: 'STRIPE_PRICE_BUNDLE_ROOM_2_120',
    grants: { extra_rooms: 2, credits: 60, panel_placement: true },
  },
  {
    id: 'unlimited',
    name: 'Unlimited',
    description: 'Every paid feature: unlimited compute, panel placement, and rooms (up to 100).',
    kind: 'unlimited',
    stripePriceEnv: 'STRIPE_PRICE_UNLIMITED',
    grants: { panel_placement: true, unlimited_compute: true, unlimited_rooms: true },
  },
];

export function offerById(id: string): StoreOffer | undefined {
  return STORE_OFFERS.find((o) => o.id === id);
}
